import { centroid, nearestPoint, point, area } from "@turf/turf";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import { each } from "lodash";
import { featureCollection } from "@turf/helpers";

import {
  POSTCODE_DISTRICT_BOUNDARIES_ID,
  POSTCODE_SECTOR_BOUNDARIES_ID,
  POSTCODE_UNIT_BOUNDARIES_ID,
} from "../consts/sources";
import { LAYERS, OUTER, POSTCODE, SECTOR } from "../consts/layers";
import {
  MIN_ZOOM_FOR_AREA_LABEL,
  FIT_BOUNDS_AREA_PADDING,
} from "../consts/map";
import { COLLECTIONS_PLAN_TYPE } from "../consts/planTypes";
import { palette } from "../theme/palette";

import { isFutureRoute } from "./routes";
import { Postcode } from "./postcode";

export const getBbox = ({ postcodes = [], lookupTable = {} }) => {
  const polygons = [];
  postcodes.forEach((i) => {
    if (lookupTable[i]?.bbox) {
      polygons.push(bboxPolygon(lookupTable[i].bbox));
    }
  });

  return polygons.length && bbox(featureCollection(polygons));
};

export const getVisiblePostcodes = (map, zoomLevel) => {
  if (!map) return;
  const features = map.queryRenderedFeatures();
  const postcodes = features
    .filter(({ layer }) => LAYERS[zoomLevel].BOUNDARIES.NAME === layer.id)
    .map(({ state }) => state.root)
    .filter((i) => i && !Array.isArray(i));
  return { postcodes };
};

export const getLevelBasedOnLayerId = (layerId) => {
  switch (layerId) {
    case LAYERS.OUTER.BOUNDARIES.NAME:
      return OUTER;
    case LAYERS.SECTOR.BOUNDARIES.NAME:
      return SECTOR;
    case LAYERS.POSTCODE.BOUNDARIES.NAME:
    default:
      return POSTCODE;
  }
};

export const getPostcodeFromFeature = (feature) => {
  if (feature.state?.root || feature.state?.outer)
    return new Postcode(feature.state.root || feature.state.outer);
  else throw new Error("No root or outer given");
};

export const heatmapMapping = {
  50: palette.heatmap[0],
  100: palette.heatmap[1],
  150: palette.heatmap[2],
  200: palette.heatmap[3],
  250: palette.heatmap[4],
  "250+": palette.heatmap[5],
};

export const getHeatmapColor = (totalStops = 0) => {
  if (totalStops > 250) return heatmapMapping["250+"];
  else if (totalStops > 200) return heatmapMapping["250"];
  else if (totalStops > 150) return heatmapMapping["200"];
  else if (totalStops > 100) return heatmapMapping["150"];
  else if (totalStops > 50) return heatmapMapping["100"];
  else if (totalStops > 0) return heatmapMapping["50"];
  else return palette.typography.white.solid;
};

export const generateLabelsForLevel = ({
  routePostcodes,
  lookupTable,
  routeLabels,
  multiPostcodes = {},
  postcodeToRoute = {},
  individual = false,
  routes = [],
  planType,
  collectionType,
  selectedTimeBand,
  centerPoint,
}) => {
  if (!lookupTable) return;
  each(Object.keys(routePostcodes), (route) => {
    const bboxes = [];
    const points = [];
    const postcodes = [...new Set(routePostcodes[route])];
    each(postcodes, (postcode) => {
      if (Object.keys(multiPostcodes).includes(postcode)) return;
      if (postcodeToRoute[postcode] !== route) return;
      if (!lookupTable[postcode]) {
        console.error(`${postcode} not included in lookupTable`);
        return;
      }
      if (lookupTable[postcode].bbox)
        bboxes.push(bboxPolygon(lookupTable[postcode].bbox));
      else if (lookupTable[postcode].center) {
        const [lng, lat] = lookupTable[postcode].center;
        bboxes.push(bboxPolygon([lng, lat, lng, lat]));
      }

      points.push(
        point(lookupTable[postcode].center || [0, 0], { route, postcode })
      );
    });

    if (points.length !== 0 && bboxes.length === 0) {
      routeLabels.push(points[0]);
      return;
    } else if (bboxes.length === 0) return;

    const futureRoute = isFutureRoute({
      route: routes.find((r) => r.routeCode === route),
      planType,
      collectionType,
      selectedTimeBand,
    });

    if (individual || futureRoute) {
      return routeLabels.push(
        ...points.map((point) => ({
          ...point,
          properties: { ...point.properties, individual: true },
        }))
      );
    }
    const bboxPol = bboxPolygon(bbox(featureCollection(bboxes)));
    if (centerPoint) {
      return routeLabels.push(
        nearestPoint(point(centerPoint), featureCollection(points))
      );
    } else {
      const centerMass = point(centroid(bboxPol).geometry.coordinates, {
        route,
      });
      return routeLabels.push(
        nearestPoint(centerMass, featureCollection(points))
      );
    }
  });

  const points = [];
  each(Object.keys(postcodeToRoute), (postcode) => {
    if (postcodeToRoute[postcode]) return;
    if (Object.keys(multiPostcodes).includes(postcode)) return;
    if (!lookupTable[postcode]) {
      console.error(`${postcode} not included in lookupTable`);
      return;
    }
    points.push(point(lookupTable[postcode].center || [0, 0], { postcode }));
  });
  routeLabels.push(...points);
};

export const getNewZoomFromScroll = (e, currentZoom) =>
  currentZoom - e.deltaY * 0.01;

export const isEditMode = ({ routeEdit, type, collectionTimeEdit }) => {
  if (routeEdit || (type === COLLECTIONS_PLAN_TYPE && collectionTimeEdit))
    return true;
  return false;
};

export const getLabelCoordinates = ({ bbox, center }) => {
  if (!bbox || !center) return null;
  const bboxPol = bboxPolygon(bbox);
  const centerPoint = point(center);
  const centerMass = point(centroid(bboxPol).geometry.coordinates);
  const {
    coordinates: [lng, lat],
  } = nearestPoint(centerMass, featureCollection([centerPoint])).geometry;
  return {
    lat,
    lng,
  };
};

export const getMinZoomForLabel = ({ area }) => {
  const key = Object.keys(MIN_ZOOM_FOR_AREA_LABEL).find((sqKm) => sqKm >= area);
  return MIN_ZOOM_FOR_AREA_LABEL[key || "MAX"];
};

export const getFitBoundsOptions = ({ bbox, targetZoomLevel }) => {
  if (!bbox || !targetZoomLevel) return null;

  const bboxPol = bboxPolygon(bbox);
  const areaSqm = area(bboxPol);

  const { innerHeight } = window;
  const key = Object.keys(FIT_BOUNDS_AREA_PADDING[targetZoomLevel]).find(
    (sqm) => sqm >= areaSqm
  );
  const divisor = FIT_BOUNDS_AREA_PADDING[targetZoomLevel][key || "MAX"];

  return {
    padding: Math.floor(innerHeight / divisor),
  };
};

export const getAdjustedZoom = ({ zoom, zoomLevel, targetZoomLevel }) => {
  if (targetZoomLevel === OUTER)
    return zoom - (zoomLevel === SECTOR ? 0.3 : 0.5);
  else if (targetZoomLevel === SECTOR)
    return zoomLevel === POSTCODE ? zoom - 0.2 : zoom + 0.2;
  else return zoom + (zoomLevel === SECTOR ? 0.1 : 0.5);
};

const SOURCE_CONFIG = {
  OUTER: {
    SOURCE: POSTCODE_DISTRICT_BOUNDARIES_ID,
    SOURCE_LAYER: LAYERS.OUTER.BOUNDARIES.SOURCE_LAYER,
  },
  SECTOR: {
    SOURCE: POSTCODE_SECTOR_BOUNDARIES_ID,
    SOURCE_LAYER: LAYERS.SECTOR.BOUNDARIES.SOURCE_LAYER,
  },
  POSTCODE: {
    SOURCE: POSTCODE_UNIT_BOUNDARIES_ID,
    SOURCE_LAYER: LAYERS.POSTCODE.BOUNDARIES.SOURCE_LAYER,
  },
};

export const setFeatureRootState = ({ map, lookupTables }) => {
  Object.keys(lookupTables).forEach((level) => {
    Object.keys(lookupTables[level]).forEach((key) => {
      const CONFIG = SOURCE_CONFIG[level];
      const id = lookupTables[level][key]?.id;
      id &&
        map.setFeatureState(
          {
            id,
            source: CONFIG.SOURCE,
            sourceLayer: CONFIG.SOURCE_LAYER,
          },
          {
            root: key,
          }
        );
    });
  });
};
